import type { RemoteDevice } from './electron-api';

/**
 * 设备中心的轻量本地偏好。只保存设备编号、收藏状态和最近使用时间，
 * 不保存凭据、设备地址或远程内容；设备在线状态始终以服务端返回为准。
 */
export interface DevicePreference {
  version: 1;
  deviceId: string;
  favorite: boolean;
  lastConnectedAt: number;
}

export const DEVICE_PREFERENCES_STORAGE_KEY = 'rc_device_preferences_v1';
export const DEVICE_PREFERENCES_LIMIT = 64;

type DeviceLike = Pick<RemoteDevice, 'deviceId'> & {
  online?: boolean;
  lastSeen?: number | null;
};

function validDeviceId(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= 128 && /^[A-Za-z0-9._:-]+$/.test(value);
}

function normalizePreference(value: unknown): DevicePreference | null {
  if (!value || typeof value !== 'object') return null;
  const item = value as Partial<DevicePreference>;
  const lastConnectedAt = item.lastConnectedAt;
  if (
    item.version !== 1 ||
    !validDeviceId(item.deviceId) ||
    typeof item.favorite !== 'boolean' ||
    typeof lastConnectedAt !== 'number' ||
    !Number.isSafeInteger(lastConnectedAt) ||
    lastConnectedAt < 0
  ) return null;
  return {
    version: 1,
    deviceId: item.deviceId,
    favorite: item.favorite,
    lastConnectedAt,
  };
}

export function normalizeDevicePreferences(value: unknown): DevicePreference[] {
  if (!Array.isArray(value)) return [];
  const byId = new Map<string, DevicePreference>();
  for (const item of value) {
    const normalized = normalizePreference(item);
    if (normalized) byId.set(normalized.deviceId, normalized);
  }
  return Array.from(byId.values())
    .sort((a, b) => {
      if (a.favorite !== b.favorite) return Number(b.favorite) - Number(a.favorite);
      return b.lastConnectedAt - a.lastConnectedAt;
    })
    .slice(0, DEVICE_PREFERENCES_LIMIT);
}

export function loadDevicePreferences(storage: Pick<Storage, 'getItem'>): DevicePreference[] {
  try {
    const raw = storage.getItem(DEVICE_PREFERENCES_STORAGE_KEY);
    return normalizeDevicePreferences(raw ? JSON.parse(raw) : []);
  } catch {
    return [];
  }
}

function saveDevicePreferences(storage: Pick<Storage, 'setItem'>, preferences: DevicePreference[]): void {
  try {
    storage.setItem(DEVICE_PREFERENCES_STORAGE_KEY, JSON.stringify(normalizeDevicePreferences(preferences)));
  } catch {
    // 隐私模式或浏览器禁用存储时，设备列表仍可正常使用。
  }
}

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function preferenceMap(): Map<string, DevicePreference> {
  const current = storage();
  return new Map(current ? loadDevicePreferences(current).map((item) => [item.deviceId, item]) : []);
}

export function isDeviceFavorite(deviceId: string): boolean {
  return Boolean(preferenceMap().get(deviceId)?.favorite);
}

export function setDeviceFavorite(deviceId: string, favorite: boolean): boolean {
  if (!validDeviceId(deviceId)) return favorite;
  const current = storage();
  if (!current) return favorite;
  const preferences = loadDevicePreferences(current);
  const previous = preferences.find((item) => item.deviceId === deviceId);
  const next: DevicePreference = {
    version: 1,
    deviceId,
    favorite,
    lastConnectedAt: previous?.lastConnectedAt || 0,
  };
  saveDevicePreferences(current, [...preferences.filter((item) => item.deviceId !== deviceId), next]);
  return favorite;
}

export function toggleDeviceFavorite(deviceId: string): boolean {
  return setDeviceFavorite(deviceId, !isDeviceFavorite(deviceId));
}

export function markDeviceConnected(deviceId: string): void {
  if (!validDeviceId(deviceId)) return;
  const current = storage();
  if (!current) return;
  const preferences = loadDevicePreferences(current);
  const previous = preferences.find((item) => item.deviceId === deviceId);
  saveDevicePreferences(current, [
    ...preferences.filter((item) => item.deviceId !== deviceId),
    {
      version: 1,
      deviceId,
      favorite: previous?.favorite || false,
      lastConnectedAt: Date.now(),
    },
  ]);
}

export function sortDevices<T extends DeviceLike>(devices: T[]): T[] {
  const preferences = preferenceMap();
  const score = (device: T): [number, number, number, string] => {
    const preference = preferences.get(device.deviceId);
    return [
      preference?.favorite ? 1 : 0,
      device.online ? 1 : 0,
      preference?.lastConnectedAt || device.lastSeen || 0,
      device.deviceId,
    ];
  };
  return [...devices].sort((a, b) => {
    const left = score(a);
    const right = score(b);
    if (right[0] !== left[0]) return right[0] - left[0];
    if (right[1] !== left[1]) return right[1] - left[1];
    if (right[2] !== left[2]) return right[2] - left[2];
    return left[3].localeCompare(right[3]);
  });
}
